/**
 * Video Reels Section Script
 * Handles the Swiper carousel of reels and the custom Quickview modal with video on the left.
 */

class VideoReelsManager {
  constructor(sectionContainer) {
    this.container = sectionContainer;
    this.sectionId = this.container.getAttribute('data-section-id');
    this.observer = null;
    this.swiper = null;

    // Quickview Modal Elements (unique inside section container)
    this.modal = this.container.querySelector('.video-quickview');
    if (this.modal) {
      this.backdrop = this.modal.querySelector('.video-quickview__backdrop');
      this.closeBtn = this.modal.querySelector('.video-quickview__close');
      this.videoPlayer = this.modal.querySelector('.video-quickview__player');
      this.loadingEl = this.modal.querySelector('.video-quickview__product-loading');
      this.detailsEl = this.modal.querySelector('.video-quickview__product-details');
    }

    this.product = null;
    this.selectedOptions = {};
    this.currentVariant = null;
    this.quantity = 1;

    this.init();
  }

  init() {
    this.initLazyLoading();
    this.initSwiper();
    this.setupQuickviewListeners();
    this.setupVideoControls();
  }

  initLazyLoading() {
    if (!('IntersectionObserver' in window)) {
      const videos = this.container.querySelectorAll('video.video-reels__video');
      videos.forEach(video => this.loadVideo(video));
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: '200px 0px',
      threshold: 0.05
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        video.__enPantalla = entry.isIntersecting;
        if (entry.isIntersecting) {
          // Se descarga solo después del primer gesto del usuario (ver layout/theme.liquid) y SOLO si se reproduce
          // solo: un reel sin reproducción automática no baja nada hasta que se pulsa play (antes 8,3 MB; T-088).
          if (!window.__holyInteraccion) return;
          if (video.getAttribute('data-autoplay') === 'true') this.playVideoIfVisible(video);
        } else {
          if (!video.paused) {
            video.pause();
          }
        }
      });
    }, observerOptions);

    const videos = this.container.querySelectorAll('video.video-reels__video');
    videos.forEach(video => {
      video.dataset.observed = 'true';
      this.observer.observe(video);
    });

    document.addEventListener('holy:interaccion', () => {
      videos.forEach(video => {
        if (!video.__enPantalla) return;
        if (video.getAttribute('data-autoplay') === 'true') this.playVideoIfVisible(video);
      });
    });
  }

  loadVideo(video) {
    if (!video) return Promise.resolve();
    video.classList.add('is-loaded');
    // Cargador único del tema (layout/theme.liquid; guarda su propia promesa): variante HLS de hasta 480p (T-088)
    return window.holyCargarVideo(video, { tope: 480 });
  }

  playVideoIfVisible(video) {
    if (!video) return;
    // Se reproduce cuando las fuentes ya están puestas (la variante HLS se elige leyendo la lista maestra) y SOLO si
    // sigue en pantalla: con un scroll rápido la carga podía terminar con el reel ya fuera y sonar sin verse
    this.loadVideo(video).then(() => { if (video.__enPantalla !== false) return video.play(); }).catch(() => {
      const toggleBtn = video.parentElement ? video.parentElement.querySelector('.video-reels__play-toggle') : null;
      if (toggleBtn) toggleBtn.classList.remove('is-playing');
    });
  }

  handleSwiperSlideUpdate(swiper) {
    if (!swiper || !swiper.slides) return;

    // Observe any new cloned videos created by Swiper loop
    if (this.observer) {
      swiper.slides.forEach(slide => {
        const video = slide.querySelector('video.video-reels__video');
        if (video && !video.dataset.observed) {
          video.dataset.observed = 'true';
          this.observer.observe(video);
        }
      });
    }

    // Preload & load active slide videos
    swiper.slides.forEach(slide => {
      const video = slide.querySelector('video.video-reels__video');
      if (!video) return;

      const isVisibleSlide = slide.classList.contains('swiper-slide-active') || 
                             slide.classList.contains('swiper-slide-next') || 
                             slide.classList.contains('swiper-slide-visible');

      // Antes se descargaban aquí las diapositivas activa y siguiente al iniciar
      // el carrusel, aunque la sección estuviera muy abajo: 2 reels de ~11 MB en
      // cada carga de la portada. Ahora solo si el reel está en pantalla y el
      // usuario ya interactuó (el IntersectionObserver se encarga del resto).
      if (isVisibleSlide && video.__enPantalla && window.__holyInteraccion && video.getAttribute('data-autoplay') === 'true') {
        this.playVideoIfVisible(video);
      }
    });
  }

  setupVideoControls() {
    // Delegated click toggling for media wrapper and play toggle button
    this.container.addEventListener('click', (e) => {
      const playBtn = e.target.closest('.video-reels__play-toggle');
      const mediaWrapper = e.target.closest('.video-reels__media-wrapper');

      if (e.target.closest('.video-reels__pill') || e.target.closest('.video-quickview')) return;

      if (playBtn || mediaWrapper) {
        const wrapper = playBtn ? playBtn.closest('.video-reels__media-wrapper') : mediaWrapper;
        if (!wrapper) return;
        const video = wrapper.querySelector('video');
        if (!video) return;

        // Un segundo toque mientras carga cancela el primero (antes encolaba otra reproducción)
        if (video.paused && !video.__quiereReproducir) {
          video.__quiereReproducir = true;
          this.loadVideo(video).then(() => { if (video.__quiereReproducir) return video.play(); })
            .catch(err => console.log('Video playback error:', err))
            .finally(() => { video.__quiereReproducir = false; });
        } else {
          video.__quiereReproducir = false;
          video.pause();
        }
      }
    });

    // Delegated play & pause event listeners (capturing phase) to keep play buttons synced
    this.container.addEventListener('play', (e) => {
      if (e.target.tagName === 'VIDEO' && e.target.classList.contains('video-reels__video')) {
        const toggleBtn = e.target.parentElement ? e.target.parentElement.querySelector('.video-reels__play-toggle') : null;
        if (toggleBtn) toggleBtn.classList.add('is-playing');
      }
    }, true);

    this.container.addEventListener('pause', (e) => {
      if (e.target.tagName === 'VIDEO' && e.target.classList.contains('video-reels__video')) {
        const toggleBtn = e.target.parentElement ? e.target.parentElement.querySelector('.video-reels__play-toggle') : null;
        if (toggleBtn) toggleBtn.classList.remove('is-playing');
      }
    }, true);
  }

  initSwiper() {
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.initSwiper(), 50);
      return;
    }

    const swiperEl = this.container.querySelector('.video-reels__swiper');
    const prevEl = this.container.querySelector('.video-reels__arrow--prev');
    const nextEl = this.container.querySelector('.video-reels__arrow--next');
    const paginationEl = this.container.querySelector('.video-reels__dots');

    if (!swiperEl) return;

    // Retrieve column and spacing properties
    const desktopCols = parseInt(this.container.getAttribute('data-cols-desktop')) || 4;
    const mobileCols = parseFloat(this.container.getAttribute('data-cols-mobile')) || 1.5;
    const desktopSpace = parseInt(this.container.getAttribute('data-space-desktop'));
    const spaceBetweenDesktop = isNaN(desktopSpace) ? 24 : desktopSpace;
    const mobileSpace = parseInt(this.container.getAttribute('data-space-mobile'));
    const spaceBetweenMobile = isNaN(mobileSpace) ? 16 : mobileSpace;

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: mobileCols,
      spaceBetween: spaceBetweenMobile,
      loop: true,
      grabCursor: true,
      watchSlidesProgress: true,
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      pagination: paginationEl ? {
        el: paginationEl,
        clickable: true,
        bulletClass: 'video-reels__dot',
        bulletActiveClass: 'is-active',
        renderBullet: function (index, className) {
          return '<button type="button" class="' + className + '" aria-label="Ir a la diapositiva ' + (index + 1) + '"></button>';
        }
      } : false,
      breakpoints: {
        768: {
          slidesPerView: Math.min(desktopCols, 2.5),
          spaceBetween: spaceBetweenDesktop,
        },
        992: {
          slidesPerView: desktopCols,
          spaceBetween: spaceBetweenDesktop,
        }
      },
      on: {
        init: (swiper) => {
          this.handleSwiperSlideUpdate(swiper);
        },
        slideChange: (swiper) => {
          this.handleSwiperSlideUpdate(swiper);
        }
      }
    });
  }

  setupQuickviewListeners() {
    if (!this.modal) return;

    // Close button click
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closeModal());
    if (this.backdrop) this.backdrop.addEventListener('click', () => this.closeModal());

    // Intercept clicks on floating product pills
    this.container.addEventListener('click', (e) => {
      const pill = e.target.closest('.video-reels__pill');
      if (pill) {
        e.preventDefault();
        e.stopPropagation();

        const handle = pill.getAttribute('data-product-handle');
        // HLS (adaptativo) si el navegador lo reproduce; si no, el MP4.
        const hlsUrl = pill.getAttribute('data-video-hls');
        const puedeHls = hlsUrl && document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
        // En el visor, variante HLS de hasta 480p (decisión de Brusly del 18-oct: ahorrar datos; con la lista maestra
        // pedía 1080p). El visor se abre al momento y el video se pone cuando llega, si sigue siendo el que se pidió.
        const urlPromesa = puedeHls && window.holyHlsVariante ? window.holyHlsVariante(hlsUrl, 480) : Promise.resolve(puedeHls ? hlsUrl : pill.getAttribute('data-video-url'));

        if (handle) {
          const turno = (this.__turnoVisor = (this.__turnoVisor || 0) + 1);
          this.openModal(handle, '', true);
          urlPromesa.then(videoUrl => { if (turno === this.__turnoVisor) this.ponerVideoVisor(videoUrl || ''); });
        }
      }
    });

    // Escape key listener when modal is open
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    });
  }

  // Pone (o quita) el video del visor; solo si el visor sigue abierto
  ponerVideoVisor(videoUrl) {
    if (!this.videoPlayer || !this.modal.classList.contains('is-open')) return;
    if (videoUrl) {
      this.videoPlayer.src = videoUrl;
      this.videoPlayer.style.display = 'block';
      this.videoPlayer.load();
      this.videoPlayer.play().catch(err => {
        console.log('Video autoplay blocked or failed:', err);
      });
    } else {
      // Sin video no se muestra reproductor (antes cargaba un video de demo externo)
      this.videoPlayer.removeAttribute('src');
      this.videoPlayer.style.display = 'none';
    }
  }

  // esperarVideo: el visor se abre ya y el video llega después (ponerVideoVisor), mientras se elige la variante HLS
  openModal(handle, videoUrl, esperarVideo = false) {
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden'; // Lock background scroll

    if (esperarVideo) {
      if (this.videoPlayer) { this.videoPlayer.pause(); this.videoPlayer.removeAttribute('src'); this.videoPlayer.style.display = 'none'; }
    } else {
      this.ponerVideoVisor(videoUrl);
    }

    // Reset quantities and states
    this.quantity = 1;
    this.detailsEl.style.display = 'none';
    this.loadingEl.style.display = 'flex';

    // Intercept mock product handle to return mocked data immediately
    if (handle === 'producto-de-ejemplo') {
      const mockProduct = {
        title: "Producto de Ejemplo",
        handle: "producto-de-ejemplo",
        featured_image: "https://cdn.shopify.com/placeholder-images/colors/product-1.png",
        price: 3800,
        compare_at_price: 4800,
        description: "Esta es la descripción de un producto de ejemplo para verificar el funcionamiento de la sección de Video Reels.",
        url: "#",
        options: [{ name: "Title", values: ["Default Title"] }],
        variants: [{
          id: 12345678,
          title: "Default Title",
          option1: "Default Title",
          price: 3800,
          compare_at_price: 4800,
          available: true,
          options: ["Default Title"]
        }]
      };
      setTimeout(() => {
        this.product = mockProduct;
        this.initializeDefaultOptions();
        this.renderProductDetails();
      }, 300);
      return;
    }

    // Fetch product details
    const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    fetch(`${rootUrl}products/${handle}.js`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch product data');
        return res.json();
      })
      .then(product => {
        this.product = product;
        this.initializeDefaultOptions();
        this.renderProductDetails();
      })
      .catch(err => {
        console.error(err);
        this.loadingEl.innerHTML = `<span style="color: #ef4444; font-weight: 600;">Error al cargar el producto.</span>`;
      });
  }

  closeModal() {
    this.modal.classList.remove('is-open');
    document.body.style.overflow = ''; // Unlock scroll

    // Stop and clear video player
    if (this.videoPlayer) {
      this.videoPlayer.pause();
      this.videoPlayer.src = '';
    }
  }

  initializeDefaultOptions() {
    this.selectedOptions = {};

    if (!this.product || !this.product.variants || this.product.variants.length === 0) {
      this.currentVariant = null;
      return;
    }

    // Default to first available variant
    let defaultVariant = this.product.variants.find(v => v.available);
    if (!defaultVariant) {
      defaultVariant = this.product.variants[0];
    }

    this.currentVariant = defaultVariant;

    if (defaultVariant && defaultVariant.options) {
      const options = this.product.options || [];
      options.forEach((opt, idx) => {
        const optName = typeof opt === 'string' ? opt : opt.name;
        this.selectedOptions[optName] = defaultVariant.options[idx];
      });
    }
  }

  formatMoney(cents) {
    return window.theme.formatMoney(cents);
  }

  renderProductDetails() {
    const p = this.product;
    const v = this.currentVariant || p;

    // Featured Image of variant or product
    let mainImg = p.featured_image;
    if (this.currentVariant && this.currentVariant.featured_image) {
      mainImg = typeof this.currentVariant.featured_image === 'string'
        ? this.currentVariant.featured_image
        : (this.currentVariant.featured_image.src || p.featured_image);
    }

    // Calculate discount percent
    let discountPercent = 0;
    if (v && v.compare_at_price > v.price) {
      discountPercent = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price);
    }

    let html = `
      <div class="video-quickview__thumb-row">
        <div class="video-quickview__thumb-info">
          ${discountPercent > 0 ? `<span class="video-quickview__badge">-${discountPercent}%</span>` : ''}
          <h4 class="video-quickview__title">${p.title}</h4>
        </div>
        ${mainImg ? `<img src="${mainImg}" alt="${p.title}" class="video-quickview__thumb">` : ''}
      </div>

      <div class="video-quickview__pricing">
        <span class="video-quickview__price">${this.formatMoney(v.price)}</span>
        ${v.compare_at_price > v.price ? `<span class="video-quickview__compare-price">${this.formatMoney(v.compare_at_price)}</span>` : ''}
      </div>

      <p class="video-quickview__desc">${p.description ? p.description.replace(/<[^>]*>/g, '').substring(0, 160) + '...' : ''}</p>
      
      <a href="${p.url}" class="video-quickview__view-link">
        Ver detalles 
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" width="12" height="12">
          <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
      </a>

      <div class="video-quickview__options">
    `;

    // Options mapping
    const options = p.options || [];
    options.forEach((option, idx) => {
      const optName = typeof option === 'string' ? option : option.name;
      const optValues = option.values || [];
      const optionNameLower = optName.toLowerCase();

      // Skip default option when there is no real variant info to show
      if (optionNameLower === 'title' && (optValues.length <= 1 || (optValues.length === 1 && optValues[0] === 'Default Title'))) {
        return;
      }

      const isColor = optionNameLower === 'color' || optionNameLower === 'colour' || optionNameLower === 'colors';
      const selectedValue = this.selectedOptions[optName] || '';

      html += `
        <div class="video-quickview__option-block" data-option-name="${optName}">
          <span class="video-quickview__option-label">${optName}: <span class="video-quickview__option-value-text">${selectedValue}</span></span>
          <div class="video-quickview__swatches">
      `;

      optValues.forEach(val => {
        const isSelected = selectedValue === val;
        const isSoldOut = !this.isOptionValueAvailable(optName, val);

        if (isColor) {
          const matchingVar = p.variants.find(item => item.options[idx] === val && item.featured_image);
          const swatchBgImage = matchingVar ? matchingVar.featured_image.src : '';

          html += `
            <button 
              type="button" 
              class="video-quickview__image-swatch ${isSelected ? 'is-selected' : ''} ${isSoldOut ? 'is-sold-out' : ''}" 
              data-value="${val.replace(/"/g, '&quot;')}"
              title="${val}"
              style="${swatchBgImage ? `background-image: url(${swatchBgImage});` : `background-color: ${val.toLowerCase()};`}"
            ></button>
          `;
        } else {
          html += `
            <button 
              type="button" 
              class="video-quickview__pill-swatch ${isSelected ? 'is-selected' : ''} ${isSoldOut ? 'is-sold-out' : ''}" 
              data-value="${val.replace(/"/g, '&quot;')}"
            >${val}</button>
          `;
        }
      });

      html += `
          </div>
        </div>
      `;
    });

    // Buy controls section
    html += `
      </div>
      
      <div class="video-quickview__buy-container">
        <span class="video-quickview__option-label">Cantidad:</span>
        <div class="video-quickview__buy-row">
          <div class="video-quickview__qty-selector">
            <button type="button" class="video-quickview__qty-btn qty-minus">−</button>
            <input type="text" class="video-quickview__qty-input" value="${this.quantity}" readonly>
            <button type="button" class="video-quickview__qty-btn qty-plus">+</button>
          </div>

          <button 
            type="button" 
            class="video-quickview__add-to-cart" 
            ${v.available ? '' : 'disabled'}
            id="VideoQuickviewSubmit"
          >
            ${v.available ? 'Añadir al carrito' : 'Agotado'}
          </button>
        </div>
      </div>
    `;

    this.loadingEl.style.display = 'none';
    this.detailsEl.innerHTML = html;
    this.detailsEl.style.display = 'flex';

    this.setupModalControls();
  }

  isOptionValueAvailable(optionName, val) {
    if (!this.product.variants) return false;
    return this.product.variants.some(v => {
      if (!v.available) return false;
      const options = this.product.options || [];
      const idx = options.findIndex(opt => {
        const optName = typeof opt === 'string' ? opt : opt.name;
        return optName === optionName;
      });
      return v.options[idx] === val;
    });
  }

  setupModalControls() {
    // Swatch Clicks
    const swatches = this.detailsEl.querySelectorAll('.video-quickview__image-swatch, .video-quickview__pill-swatch');
    swatches.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const block = btn.closest('.video-quickview__option-block');
        const optionName = block.getAttribute('data-option-name');
        const val = btn.getAttribute('data-value');

        this.selectedOptions[optionName] = val;
        this.updateVariantMatch();
        this.renderProductDetails();
      });
    });

    // Quantity selectors
    const minus = this.detailsEl.querySelector('.qty-minus');
    const plus = this.detailsEl.querySelector('.qty-plus');
    const qtyInput = this.detailsEl.querySelector('.video-quickview__qty-input');

    if (minus && plus && qtyInput) {
      minus.addEventListener('click', () => {
        if (this.quantity > 1) {
          this.quantity--;
          qtyInput.value = this.quantity;
        }
      });

      plus.addEventListener('click', () => {
        this.quantity++;
        qtyInput.value = this.quantity;
      });
    }

    // Ajax add to cart
    const submit = this.detailsEl.querySelector('#VideoQuickviewSubmit');
    if (submit) {
      submit.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.currentVariant) return;

        submit.disabled = true;
        submit.textContent = 'Agregando...';

        const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
        fetch(`${rootUrl}cart/add.js?skip_sync=1`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new URLSearchParams({
            'id': this.currentVariant.id,
            'quantity': this.quantity
          }).toString()
        })
          .then(res => {
            if (!res.ok) throw new Error('Cart submission failed');
            return res.json();
          })
          .then(() => {
            submit.textContent = '¡Añadido!';

            if (window.refreshCartDrawer) {
              window.refreshCartDrawer(true);
            }

            setTimeout(() => {
              this.closeModal();
            }, 600);
          })
          .catch(err => {
            console.error(err);
            submit.textContent = 'Error';
            submit.disabled = false;
          });
      });
    }
  }

  updateVariantMatch() {
    if (!this.product.variants) return;
    const options = this.product.options || [];
    const match = this.product.variants.find(v => {
      return options.every((opt, idx) => {
        const optName = typeof opt === 'string' ? opt : opt.name;
        return v.options[idx] === this.selectedOptions[optName];
      });
    });

    if (match) {
      this.currentVariant = match;
    }
  }
}

// Global initialization
function initAllVideoReels() {
  const sections = document.querySelectorAll('.video-reels-section-wrapper');
  sections.forEach(sec => {
    if (!sec.classList.contains('js-video-reels-initialized')) {
      new VideoReelsManager(sec);
      sec.classList.add('js-video-reels-initialized');
    }
  });
}

document.addEventListener('DOMContentLoaded', initAllVideoReels);
document.addEventListener('shopify:section:load', (e) => {
  const sectionWrapper = e.target.querySelector('.video-reels-section-wrapper');
  if (sectionWrapper) {
    new VideoReelsManager(sectionWrapper);
    sectionWrapper.classList.add('js-video-reels-initialized');
  }
});

// Run in case DOMContentLoaded fired before execution
if (document.readyState !== 'loading') {
  initAllVideoReels();
}
